var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { Text } from '@ingka/common-styles-webc';
import { archiveBox, pencil, registerIcon } from '@ingka/icon-store';
import { Image as SkapaImage } from '@ingka/image-webc/passive.js';
import { LoadingBall as SkapaLoadingBall } from '@ingka/loading-webc/passive.js';
import '@inter-ikea-kompis/component-text';
import DesignCardSizeEnum from '../../enums/DesignCardSizeEnum.js';
import DesignCardStyle from './DesignCardStyle.js';
/**
 * This component is a representation of a saved design in the form of a clickable card.
 *
 * @event click Triggered when the design card is clicked.
 */
export default class DesignCard extends KompisElement {
    constructor() {
        super(...arguments);
        // Sets the loading state for the design card. The image is replaced with loading-ball and the whole card will be disabled
        this.loading = false;
        // Label for the design card to set as an aria-label for accessibility reasons
        this.allyLabel = '';
        // The image to show in the design card
        this.imageUrl = null;
        // The alt-text for the image
        this.imageAltText = null;
        // Title
        this.title = '';
        // Subtitle
        this.subtitle = null;
        // Sets the card size. Defaults to big.
        this.size = DesignCardSizeEnum.big;
    }
    async connectedCallback() {
        registerIcon(archiveBox);
        registerIcon(pencil);
        this.customElements.define('skapa-loading-ball', SkapaLoadingBall);
        this.customElements.define('skapa-image', SkapaImage);
        super.connectedCallback();
    }
    render() {
        return html `
			<style>
				${DesignCardStyle.getStyle()}
			</style>

			<div
				role="button"
				tabindex="${this.loading ? -1 : 0}"
				class="container"
				data-testid="kompis-design-card"
				aria-label="${this.allyLabel}"
				aria-disabled="${this.loading}"
				@click="${this.onClick.bind(this)}"
				@keydown="${this.onKeydown.bind(this)}"
			>
				<div class="content-container">
					${this.size === DesignCardSizeEnum.big
            ? html `
								<div class="image-container">
									<skapa-image>
										<img
											class="${this.loading ? 'invisible' : ''} image"
											src="${this.imageUrl}"
											alt="${this.imageAltText}"
											draggable="false"
										/>
										${this.loading
                ? html `<div data-testid="loading-ball" class="loading-ball-container">
													<skapa-loading-ball medium> </skapa-loading-ball>
												</div>`
                : ''}
									</skapa-image>
								</div>
							`
            : ''}
				</div>
				<div class="footer">
					${this.size === DesignCardSizeEnum.big
            ? ''
            : html `<div class="relative footer-image-size">
								<skapa-image>
									<img
										class="${this.loading ? 'invisible' : ''} footer-image-size footer-image"
										src="${this.imageUrl}"
										alt="${this.imageAltText}"
										draggable="false"
									/>
									${this.loading
                ? html `<div data-testid="loading-ball" class="loading-ball-container">
												<skapa-loading-ball medium> </skapa-loading-ball>
											</div>`
                : ''}
								</skapa-image>
							</div>`}

					<div
						class="text-container"
						style="${this.size === DesignCardSizeEnum.big ? 'width: 100%' : ''}"
					>
						<h4 class="title truncate ${Text.classnames({ heading: { xs: true } })}">
							${this.title}
						</h4>
						<p class="subtitle truncate ${Text.classnames({ body: { m: true } })}">
							${this.subtitle}
						</p>
					</div>
				</div>
				<div class="outline"></div>
			</div>
		`;
    }
    onClick(e) {
        e.stopPropagation();
        if (this.loading)
            return;
        this.dispatchEvent(new CustomEvent('click', {
            bubbles: true
        }));
    }
    onKeydown(e) {
        e.stopPropagation();
        e.preventDefault();
        if (this.loading || (e instanceof KeyboardEvent && e.key !== 'Enter' && e.key !== ' ')) {
            return;
        }
        this.dispatchEvent(new CustomEvent('click', {
            bubbles: true
        }));
    }
}
__decorate([
    property({ type: Boolean })
], DesignCard.prototype, "loading", void 0);
__decorate([
    property({ type: String })
], DesignCard.prototype, "allyLabel", void 0);
__decorate([
    property({ type: String })
], DesignCard.prototype, "imageUrl", void 0);
__decorate([
    property({ type: String })
], DesignCard.prototype, "imageAltText", void 0);
__decorate([
    property({ type: String })
    // eslint-disable-next-line lit/no-native-attributes
], DesignCard.prototype, "title", void 0);
__decorate([
    property({ type: String })
], DesignCard.prototype, "subtitle", void 0);
__decorate([
    property({ type: String })
], DesignCard.prototype, "size", void 0);
//# sourceMappingURL=DesignCard.js.map